import React, {useContext} from 'react';
import {useIntl} from 'react-intl';
import QuantitySelector from '@homeaway/react-quantity';
import {
    TrackingContextValue,
    TrackingContext,
} from '@homeaway/react-event-tracking';

import FilterGroupFilters from '../FilterGroupFilters/FilterGroupFilters';
import {Props as FilterGroupContentProps} from '../FilterGroupContent/FilterGroupContent';

import {
    getBedroomLabel,
    getBathroomLabel,
    getBathroomValue,
    getBedroomValue,
} from '../../utils/roomsAndSpacesUtils';

import {ROOMSANDSPACES_FILTER_GROUP_ID} from '../../constants/filters';
import {GENERIC_EVENT} from '../../utils/edap';

import './RoomsAndSpacesFilter.less';

type LabelComponentProps = {
    data: {
        label: string;
        value: string;
    };
};

type Props = Pick<
    FilterGroupContentProps,
    | 'rank'
    | 'customFiltersParams'
    | 'filters'
    | 'onFilterToggle'
    | 'resetViewAll'
    | 'isMobile'
    | 'onCustomFilterUpdate'
    | 'selectedFilters'
    | 'twoColumns'
>;

export const LabelComponent = ({data: {label, value}}: LabelComponentProps) => {
    const labelComp =
        label !== '' ? (
            <span className="RoomsAndSpacesFilter__label">{label}</span>
        ) : null;
    return (
        <div>
            <span className="RoomsAndSpacesFilter__value">{value}</span>
            {labelComp}
        </div>
    );
};

export default function RoomsAndSpacesFilter({
    customFiltersParams,
    filters,
    isMobile,
    onCustomFilterUpdate,
    onFilterToggle,
    selectedFilters,
    rank,
    resetViewAll,
    twoColumns,
}: Props) {
    const edapContext: TrackingContextValue = useContext(TrackingContext);
    const intl = useIntl();
    const {formatMessage} = intl;

    const handleBedroomCountChange = (minBedrooms: number | null) => {
        edapContext.trigger(GENERIC_EVENT, {
            eventaction: 'filter selected',
            eventlabel: `bedrooms_min_${minBedrooms}`,
        });

        onCustomFilterUpdate({
            minBedrooms,
            maxBedrooms: null,
        });
    };

    const handleBathroomCountChange = (minBathrooms: number | null) => {
        edapContext.trigger(GENERIC_EVENT, {
            eventaction: 'filter selected',
            eventlabel: `bathrooms_min_${minBathrooms}`,
        });

        onCustomFilterUpdate({
            minBathrooms,
            maxBathrooms: null,
        });
    };

    const {selectedMinBedrooms, selectedMinBathrooms} = customFiltersParams;
    const bathroomValue = getBathroomValue(selectedMinBathrooms, formatMessage);
    const bedroomValue = getBedroomValue(selectedMinBedrooms, formatMessage);
    return (
        <div
            role="menuitem"
            aria-label={'filter-group-label-RoomsAndSpacesFilter'}
            className={`RoomsAndSpacesFilter`}
            data-wdio="RoomsAndSpacesFilter"
        >
            <div className="RoomsAndSpacesFilter__QuantitySelector form-group">
                <QuantitySelector
                    id="bedrooms"
                    increaseLabel={formatMessage({
                        id: 'bedroomsFilterIncreaseLabel',
                        defaultMessage: 'Increase bedrooms',
                        description:
                            'Increases the amount of bedrooms' +
                            ' to search for',
                    })}
                    decreaseLabel={formatMessage({
                        id: 'bedroomsFilterDecreaseLabel',
                        defaultMessage: 'Decrease bedrooms',
                        description:
                            'Decreases the amount of bedrooms to search for',
                    })}
                    value={selectedMinBedrooms}
                    onIncrement={handleBedroomCountChange}
                    onDecrement={handleBedroomCountChange}
                    max={50}
                    label={LabelComponent}
                    labelData={{
                        value: bedroomValue,
                        label: getBedroomLabel(formatMessage),
                    }}
                />
            </div>
            <div className="RoomsAndSpacesFilter__QuantitySelector form-group">
                <QuantitySelector
                    id="bathrooms"
                    increaseLabel={formatMessage({
                        id: 'bathroomsFilterIncreaseLabel',
                        defaultMessage: 'Increase bathrooms',
                        description:
                            'Increases the amount of bathrooms' +
                            ' to search for',
                    })}
                    decreaseLabel={formatMessage({
                        id: 'bathroomsFilterDecreaseLabel',
                        defaultMessage: 'Decrease bathrooms',
                        description:
                            'Decreases the amount of bathrooms to search for',
                    })}
                    value={selectedMinBathrooms}
                    onIncrement={handleBathroomCountChange}
                    onDecrement={handleBathroomCountChange}
                    max={50}
                    label={LabelComponent}
                    labelData={{
                        value: bathroomValue,
                        label: getBathroomLabel(formatMessage),
                    }}
                />
            </div>
            <FilterGroupFilters
                filters={filters}
                groupId={ROOMSANDSPACES_FILTER_GROUP_ID}
                selectedFilters={selectedFilters}
                onFilterToggle={onFilterToggle}
                resetViewAll={resetViewAll}
                isMobile={isMobile}
                showMax={4}
                twoColumns={twoColumns}
                rank={rank}
            />
        </div>
    );
}
